/*global chrome*/
import { sha256 } from 'js-sha256';
import { CHUNK_SIZE, BOT_API_URL } from './config.js'; // Mengambil chunkSize & alamat bot dari config
import IndexBackupManager from './index-backup.js'; // Backup index otomatis ke Discord (lewat bot)
import { normalize, extensionOf } from './search.js';
import { loadTree, saveTree, isLegacyFormat, LOCAL_MAX_CHARS } from './tree-store.js';
import { withToken, getAuthToken, reportUploadTooLarge } from './api.js';
import {
    uploadIdFor, getPendingUpload, savePendingUpload, recordUploadedChunk, removePendingUpload, listPendingUploads,
} from './upload-store.js';
import {
    ENC_OVERHEAD, deriveKeyBytes, keyBytesToString, keyBytesFromString, makeCipher,
    encryptBlob, decryptBlob, makeCheck, verifyCheck,
} from './crypto-utils.js';

export const FILE_DELIMITER = '/';
// Folder khusus (tersembunyi dari daftar folder biasa) tempat item yang
// "dihapus" disimpan sementara - supaya delete bisa dibatalkan (Restore)
// sebelum benar-benar dihapus permanen dari Discord.
export const TRASH_NAME = '.trash';
export const TRASH_PATH = FILE_DELIMITER + TRASH_NAME;

async function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

function abortError() {
    const error = new Error('Upload dijeda.');
    error.name = 'AbortError';
    return error;
}

// Ukuran potongan upload (sama dengan discord-bot/upload-limit.js). Tangga
// dipakai kalau bot tidak bisa dihubungi saat Discord menolak potongan.
const MiB = 1024 * 1024;
const MIN_CHUNK_BYTES = 1 * MiB;
const CHUNK_LADDER = [95, 47, 23, 19, 9, 7, 4, 1].map((mb) => mb * MiB);
const MAX_INFLIGHT_BYTES = 256 * MiB;

function isTooLargeError(error) {
    return Boolean(error) && (error.status === 413 || /"code":\s*40005\b/.test(String(error.message)));
}

// Identitas file yang tetap sama walau file di-rename/dipindah: chunk
// pertamanya di Discord (unik per upload). Dipakai progres tontonan, dll.
export function fileKeyOf(node) {
    if (!node || node.type === 'directory' || typeof node.content !== 'string' || node.content === '') return null;
    try {
        const refs = JSON.parse(node.content);
        if (Array.isArray(refs) && refs.length > 0) {
            const first = refs[0];
            const id = first && typeof first === 'object' ? first.id : first;
            if (id) return `c:${id}:${refs.length}`;
        }
    } catch { /* content rusak */ }
    return null;
}

// Menjalankan `mapper` untuk tiap item di `items` SECARA PARALEL (dibatasi
// jumlah `concurrency` sekaligus), sambil tetap menjaga urutan hasil sesuai
// urutan item aslinya (penting untuk chunk file, karena harus digabung
// sesuai urutan byte-nya). Dipakai untuk mempercepat preview video/gambar,
// yang sebelumnya mengambil chunk SATU PER SATU (sekuensial) sehingga lambat
// terutama untuk video besar dengan banyak chunk.
async function parallelMap(items, concurrency, mapper) {
    const results = new Array(items.length);
    let nextIndex = 0;
    async function worker() {
        while (nextIndex < items.length) {
            const current = nextIndex++;
            results[current] = await mapper(items[current], current);
        }
    }
    const workerCount = Math.max(1, Math.min(concurrency, items.length));
    await Promise.all(Array.from({ length: workerCount }, () => worker()));
    return results;
}

async function fetchUrlFromExtension(url) {
    return new Promise((resolve) => {
        try {
            chrome.runtime.sendMessage("jklpfhklkhbfgeencifbmkoiaokeieah", { message: { url: url } }, response => {
                if (!response || !("data" in response)) {
                    resolve(null);
                    return;
                }
                resolve(response.data);
            });
        } catch {
            resolve(null);
        }
    });
}

function isBotProxyConfigured() {
    return typeof BOT_API_URL === "string" && BOT_API_URL.length > 0 && !BOT_API_URL.includes("XXXX") && Boolean(getAuthToken());
}

// fetch() browser juga tidak punya timeout bawaan - kalau bot lokal macet
// (bukan error, cuma diam), request ini bisa nyangkut selamanya dan preview
// video kelihatan "stuck" di suatu persen tanpa pernah gagal ataupun
// lanjut ke fallback di bawah. Timeout ini cuma untuk fase "dapat balasan
// header dari bot" - bot sendiri juga sudah punya timeout untuk fase
// stream-nya (lihat PROXY_STALL_TIMEOUT_MS di discord-bot/index.js).
async function fetchWithTimeout(url, timeoutMs) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        return await fetch(url, { signal: controller.signal });
    } finally {
        clearTimeout(timer);
    }
}

// Cara PALING diandalkan untuk mengambil file dari CDN Discord tanpa kena
// CORS: minta bot Disbox (yang jalan di komputer sendiri, tidak kena CORS)
// yang mengambilkannya. Jauh lebih stabil dibanding proxy publik pihak
// ketiga di bawah, yang sering gagal/rate-limited.
async function fetchUrlFromBotProxy(url) {
    if (!isBotProxyConfigured()) return null;
    try {
        const response = await fetchWithTimeout(withToken(`${BOT_API_URL}/proxy?url=${encodeURIComponent(url)}`), 15_000);
        if (!response.ok) return null;
        return response;
    } catch {
        return null; // Bot sedang tidak jalan / tidak bisa dihubungi / timeout - lanjut ke fallback.
    }
}

async function fetchUrlFromProxy(url) {
    return await fetch(`https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`);
}

// Mengambil satu chunk (URL CDN Discord) sebagai Blob.
export async function fetchUrl(url) {
    // Urutan: 1) bot Disbox sendiri (paling stabil) -> 2) extension Chrome
    // kalau ada -> 3) proxy publik pihak ketiga sebagai upaya terakhir.
    const botResponse = await fetchUrlFromBotProxy(url);
    if (botResponse !== null) {
        return await botResponse.blob();
    }
    const extensionResult = await fetchUrlFromExtension(url);
    if (extensionResult !== null) {
        return await (await fetch(extensionResult)).blob();
    }
    return await (await fetchUrlFromProxy(url)).blob();
}

// `decrypt` (opsional): fungsi blob -> blob untuk file terenkripsi, dipanggil
// per chunk. File lama yang tidak terenkripsi dipanggil tanpa `decrypt`.
export async function downloadFromAttachmentUrls(attachmentUrls, writeStream, onProgress = null, fileSize = -1, decrypt = null) {
    let bytesDownloaded = 0;
    if (onProgress) {
         onProgress(0, fileSize);
    }
    for (let attachmentUrl of attachmentUrls) {
        let blob = await fetchUrl(attachmentUrl);
        if (decrypt) blob = await decrypt(blob);
        await writeStream.write(blob);
        bytesDownloaded += blob.size;
        if (onProgress) {
            onProgress(bytesDownloaded, fileSize);
        }
    }
    await writeStream.close();
}

export async function blobFromAttachmentUrls(attachmentUrls, mimeType, onProgress = null, fileSize = -1, concurrency = 6, decrypt = null) {
    let bytesDownloaded = 0;
    if (onProgress) {
        onProgress(0, fileSize);
    }
    // Diambil PARALEL (bukan satu-satu) - ini yang paling berpengaruh ke
    // kecepatan preview video, karena sebelumnya harus menunggu tiap chunk
    // selesai diunduh sebelum mulai mengunduh chunk berikutnya.
    const chunks = await parallelMap(attachmentUrls, concurrency, async (attachmentUrl) => {
        let blob = await fetchUrl(attachmentUrl);
        if (decrypt) blob = await decrypt(blob);
        bytesDownloaded += blob.size;
        if (onProgress) {
            onProgress(bytesDownloaded, fileSize);
        }
        return blob;
    });
    return new Blob(chunks, { type: mimeType });
}

class DiscordWebhookClient {
    constructor(webhookUrl) {
        const id = webhookUrl.split('/').slice(0, -1).pop();
        const token = webhookUrl.split('/').pop();
        // Dulu pakai domain lama "discordapp.com" yang selalu di-redirect ke
        // "discord.com" - itu artinya SETIAP request (upload & baca chunk)
        // kena 1 round-trip tambahan sia-sia. Langsung pakai domain aslinya
        // supaya lebih cepat, apalagi untuk file yang kepecah jadi banyak chunk.
        this.baseUrl = `https://discord.com/api/webhooks/${id}/${token}`;
        this.rateLimitWaits = {};
    }
    // Request ke Discord dengan pengulangan otomatis. Dulu satu kali koneksi
    // putus ("Failed to fetch") atau Discord sedang error 5xx langsung
    // menggagalkan SELURUH upload file besar. Sekarang dicoba ulang dengan
    // jeda yang makin lama (total sekitar 1 menit) sebelum menyerah - dan
    // kalaupun tetap gagal, upload bisa dilanjutkan dari potongan terakhir.
    async fetchFromApi(path, { type, method, body, signal }) {
        let networkRetries = 0;
        let serverRetries = 0;
        let rateLimitRetries = 0;
        for (;;) {
            if (signal && signal.aborted) throw abortError();
            if (this.rateLimitWaits[type] > 0) {
                console.log(`Waiting ${this.rateLimitWaits[type]}ms for rate limit to reset`);
                await sleep(this.rateLimitWaits[type]);
            }
            let response;
            try {
                response = await fetch(`${this.baseUrl}${path}`, { method, body, signal });
            } catch (error) {
                if ((signal && signal.aborted) || networkRetries >= 6) {
                    throw signal && signal.aborted ? abortError() : new Error(`Koneksi ke Discord terputus (${error.message}).`);
                }
                const wait = Math.min(1000 * 2 ** networkRetries, 20000);
                networkRetries++;
                console.warn(`[Disbox] ${type} gagal (${error.message}), dicoba lagi dalam ${wait / 1000} detik (percobaan ${networkRetries}/6)`);
                await sleep(wait);
                continue;
            }
            const headers = response.headers;
            const remainingRequests = Number(headers.get("X-RateLimit-Remaining"));
            const resetAfter = Number(headers.get("X-RateLimit-Reset-After"));
            this.rateLimitWaits[type] = remainingRequests === 0 ? resetAfter * 1000 : 0;
            const status = response.status;
            if (status === 429 && rateLimitRetries++ < 30) {
                const responseJson = await response.json().catch(() => ({}));
                const retryAfter = Number(responseJson.retry_after) || 1;
                this.rateLimitWaits[type] = retryAfter * 1000;
                console.log("Rate limit exceeded, retrying");
                continue;
            }
            if (status >= 500 && serverRetries < 4) {
                serverRetries++;
                await response.arrayBuffer().catch(() => {});
                await sleep(1000 * 2 ** serverRetries);
                continue;
            }
            if (status >= 400) {
                const error = new Error(`Failed to ${type} with status ${status}: ${await response.text()}`);
                error.status = status;
                throw error;
            }
            return response;
        }
    }
    async sendAttachment(filename, blob, signal) {
        const formData = new FormData();
        formData.append('payload_json', JSON.stringify({}));
        formData.append('file', blob, filename);
        const response = await this.fetchFromApi("?wait=true", {
            type: "sendAttachment",
            method: 'POST',
            body: formData,
            signal,
        });
        return await response.json();
    }
    async getMessage(id) {
        const response = await this.fetchFromApi(`/messages/${id}`, {
            type: "getMessage",
            method: 'GET',
        });
        return await response.json();
    }
    async deleteMessage(id) {
        await this.fetchFromApi(`/messages/${id}`, {
            type: "deleteMessage",
            method: 'DELETE',
        });
    }
}

class DiscordFileStorage {
    constructor(webhookUrls) {
        this.webhookClients = webhookUrls.map(url => new DiscordWebhookClient(url.trim()));
        this.uploadWebhooks = null;
        this.chunkBytes = CHUNK_SIZE;
    }

    // Ukuran potongan untuk upload baru, dari setelan akun di bot (bisa turun
    // otomatis kalau Discord menurunkan batas upload, atau dinaikkan dari Setelan).
    setChunkSize(bytes) {
        const n = Math.floor(Number(bytes));
        this.chunkBytes = n >= MIN_CHUNK_BYTES ? n : CHUNK_SIZE;
    }

    // Webhook bisa ditambah dari Setelan. Daftar lama TIDAK diubah urutannya
    // (tiap potongan file mencatat nomor webhook yang mengunggahnya), cuma
    // ditambah di ujung.
    setWebhookUrls(webhookUrls) {
        for (let i = this.webhookClients.length; i < webhookUrls.length; i++) {
            this.webhookClients.push(new DiscordWebhookClient(webhookUrls[i].trim()));
        }
    }

    // Berapa webhook (dari urutan pertama) yang dipakai untuk upload BARU.
    // Kosong = semua. Baca/hapus file lama tetap lewat webhook yang dulu
    // mengunggahnya, jadi setelan ini tidak memengaruhi file lama.
    setUploadWebhookCount(count) {
        const n = Math.floor(Number(count));
        this.uploadWebhooks = n > 0 ? n : null;
    }

    getUploadWebhookCount() {
        const total = this.webhookClients.length;
        return Math.max(1, Math.min(total, this.uploadWebhooks || total));
    }

    // Mengambil URL berdasarkan webhook yang tepat
    async getAttachmentUrls(messageRefs) {
        // Diparalelkan (sebelumnya sekuensial satu-satu) - untuk file dengan
        // banyak chunk (video besar dll), ini bisa memangkas waktu tunggu
        // secara signifikan sebelum proses download/preview-nya mulai.
        const concurrency = Math.max(1, this.webhookClients.length);
        return await parallelMap(messageRefs, concurrency, async (ref) => {
            // Cek apakah format baru (object) atau format lama (string ID)
            let id = typeof ref === 'object' ? ref.id : ref;
            let clientIndex = typeof ref === 'object' ? ref.clientIndex : 0;

            // Gunakan client yang mengupload, atau fallback ke client pertama
            const client = this.webhookClients[clientIndex] || this.webhookClients[0];
            const message = await client.getMessage(id);

            if (message && message.attachments && message.attachments.length > 0) {
                return message.attachments[0].url;
            }
            throw new Error(`Gagal memuat URL preview/download untuk chunk ID: ${id}`);
        });
    }
    
    // Menyimpan index webhook mana yang bertugas mengupload tiap chunk
    // `cipher` (opsional): kalau ada, tiap chunk dienkripsi sebelum dikirim. Ukuran
    // chunk asli dikurangi ENC_OVERHEAD supaya chunk terenkripsi tetap muat di
    // batas upload Discord.
    //
    // options:
    // - existingRefs: potongan yang SUDAH terkirim di percobaan sebelumnya
    //   (lanjutkan upload), dilewati.
    // - chunkSize: ukuran potongan (harus sama dengan percobaan sebelumnya).
    // - onChunkDone(index, ref): dipanggil tiap potongan selesai (dicatat supaya
    //   bisa dilanjutkan kalau upload putus).
    // - signal: AbortSignal untuk menjeda upload.
    //
    // Kalau Discord menolak potongan karena terlalu besar, error-nya diberi
    // tanda `tooLarge` dan `chunkBytes` (ukuran yang ditolak).
    async upload(sourceFile, namePrefix, onProgress = null, cipher = null, options = {}) {
        const plainChunkSize = options.chunkSize || (cipher ? this.chunkBytes - ENC_OVERHEAD : this.chunkBytes);
        const totalChunks = Math.ceil(sourceFile.size / plainChunkSize);
        const messageRefs = new Array(totalChunks);
        const existing = options.existingRefs || {};
        const queue = [];
        let uploadedBytes = 0;
        for (let i = 0; i < totalChunks; i++) {
            if (existing[i]) {
                messageRefs[i] = existing[i];
                uploadedBytes += Math.min(plainChunkSize, sourceFile.size - i * plainChunkSize);
            } else {
                queue.push(i);
            }
        }
        if (onProgress) onProgress(uploadedBytes, sourceFile.size);

        let failure = null;
        const signal = options.signal;
        // Upload paralel: satu worker per webhook yang dipilih di Setelan.
        const uploadCount = this.getUploadWebhookCount();
        const worker = async (workerIndex) => {
            while (queue.length > 0 && !failure) {
                if (signal && signal.aborted) {
                    failure = failure || abortError();
                    break;
                }
                const index = queue.shift();
                // Tiap worker punya webhook sendiri (dari webhook yang dipilih untuk upload).
                const clientIndex = workerIndex % uploadCount;
                const client = this.webhookClients[clientIndex];
                const chunk = sourceFile.slice(index * plainChunkSize, (index + 1) * plainChunkSize);
                let payload = chunk;
                try {
                    if (cipher) payload = await encryptBlob(cipher, chunk);
                    const result = await client.sendAttachment(`${namePrefix}_${index}`, payload, signal);
                    // Simpan ID beserta Index Webhook-nya
                    messageRefs[index] = { id: result.id, clientIndex: clientIndex };
                    if (options.onChunkDone) options.onChunkDone(index, messageRefs[index]);
                    uploadedBytes += chunk.size;
                    if (onProgress) onProgress(uploadedBytes, sourceFile.size);
                } catch (error) {
                    if (isTooLargeError(error)) {
                        error.tooLarge = true;
                        error.chunkBytes = payload.size;
                    }
                    // Worker lain menyelesaikan potongan yang sedang dikirimnya
                    // (tetap tercatat), lalu berhenti.
                    failure = failure || error;
                }
            }
        };

        // Potongan besar = lebih sedikit upload paralel, supaya memori browser
        // (tiap potongan dibaca dan dienkripsi utuh) tidak membengkak.
        const workerCount = Math.max(1, Math.min(uploadCount, Math.floor(MAX_INFLIGHT_BYTES / plainChunkSize)));
        const workers = [];
        for (let i = 0; i < workerCount; i++) {
            workers.push(worker(i));
        }
        await Promise.all(workers);
        if (failure) throw failure;
        return messageRefs; // Mengembalikan array of objects
    }
    
    async download(messageRefs, writeStream, onProgress = null, fileSize = -1, decrypt = null) {
        const attachmentUrls = await this.getAttachmentUrls(messageRefs);
        await downloadFromAttachmentUrls(attachmentUrls, writeStream, onProgress, fileSize, decrypt);
    }
    
    // Proses penghapusan dengan webhook yang tepat dan Try-Catch untuk Error 404
    async delete(messageRefs, onProgress) {
        let chunksDeleted = 0;
        if (onProgress) {
            onProgress(0, messageRefs.length);
        }
        for (let ref of messageRefs) {
            // Sama seperti getAttachmentUrls, pisahkan ID dan Index Webhook
            let id = typeof ref === 'object' ? ref.id : ref;
            let clientIndex = typeof ref === 'object' ? ref.clientIndex : 0;
            
            const client = this.webhookClients[clientIndex] || this.webhookClients[0];
            
            try {
                await client.deleteMessage(id);
            } catch (error) {
                console.warn(`Chunk ${id} gagal dihapus dari Discord. Mungkin sudah terhapus sebelumnya. Melanjutkan proses...`);
            }
            
            chunksDeleted++;
            if (onProgress) {
                onProgress(chunksDeleted, messageRefs.length);
            }
        }
    }
}

class DisboxFileManager {
    static async create(webhookUrls) {
        // Ambil URL pertama untuk generate UserId agar sinkron dengan yang lama
        const firstUrl = new URL(webhookUrls[0].trim());
        const userId = sha256(firstUrl.href);

        const legacy = isLegacyFormat(userId);
        const stored = await loadTree(userId);
        const fileTree = stored ? stored.tree : { id: 1, name: "root", type: "directory", children: {} };
        const manager = new this(userId, new DiscordFileStorage(webhookUrls), fileTree);
        manager._lastPersistChars = stored ? stored.chars : 0;
        if (!stored) {
            // Belum ada daftar tersimpan di browser ini: diambil utuh dari bot.
            manager._setBase(null);
            manager._setDirty(false);
            manager._persistTree(0);
        } else if (stored.source === 'idb') {
            // Catatan sinkron mengikuti versi yang benar-benar tersimpan bersama tree.
            manager._setBase(stored.base);
        } else if (legacy && (!manager.isDirty() || stored.chars > LOCAL_MAX_CHARS)) {
            // Versi lama web ini gagal menyimpan tree besar ke localStorage tapi
            // tetap mencatat versi barunya, jadi tree tersimpan bisa lebih tua
            // dari catatannya. Sekali saja: dasar dianggap tidak diketahui,
            // tree diambil ulang dari bot (perubahan lokal yang belum
            // terkirim, kalau ada, digabung tanpa menghapus apa pun).
            manager._setBase(null);
            if (!manager.isDirty()) localStorage.setItem(`disbox_${userId}_savedAt`, '0');
        }
        await manager.loadStoredCipher();
        return manager;
    }

    // ===================== Enkripsi file baru =====================
    // Kalau kunci enkripsi ada di perangkat ini, SETIAP file yang diupload
    // sesudahnya dienkripsi. File lama tanpa tanda `enc` tetap dibaca apa adanya.
    _encKeyStorageKey() {
        return `disbox_${this.userId}_enckey`;
    }
    async loadStoredCipher() {
        this.cipher = null;
        try {
            const stored = localStorage.getItem(this._encKeyStorageKey());
            if (stored) {
                this.cipher = await makeCipher(keyBytesFromString(stored));
            }
        } catch (e) {
            console.warn('[Disbox] Gagal memuat kunci enkripsi:', e);
        }
    }
    isEncryptionEnabled() {
        return !!this.cipher;
    }
    // Mengaktifkan enkripsi dengan passphrase. Kalau index sudah punya tanda
    // pemeriksa (dibuat saat pertama kali diaktifkan, di web ATAU di bot), passphrase
    // harus cocok; kalau belum, tanda itu dibuat sekarang.
    async enableEncryption(passphrase) {
        if (!passphrase || passphrase.length < 8) {
            throw new Error('Passphrase minimal 8 karakter.');
        }
        const keyBytes = await deriveKeyBytes(passphrase, this.userId);
        const cipher = await makeCipher(keyBytes);
        if (this.fileTree.encCheck) {
            if (!(await verifyCheck(cipher, this.fileTree.encCheck))) {
                throw new Error('Passphrase salah (tidak sama dengan yang dipakai sebelumnya untuk akun ini).');
            }
        } else {
            this.fileTree.encCheck = await makeCheck(cipher);
            this._saveTree();
        }
        localStorage.setItem(this._encKeyStorageKey(), keyBytesToString(keyBytes));
        this.cipher = cipher;
    }
    // Hanya melepas kunci dari perangkat ini: upload berikutnya tidak dienkripsi
    // lagi, dan file terenkripsi tidak bisa dibuka sampai passphrase dimasukkan lagi.
    disableEncryption() {
        localStorage.removeItem(this._encKeyStorageKey());
        this.cipher = null;
    }
    // Fungsi dekripsi per chunk untuk sebuah file, atau null kalau file itu tidak terenkripsi.
    getDecryptor(file) {
        if (!file || !file.enc) return null;
        if (!this.cipher) {
            throw new Error('File ini terenkripsi. Aktifkan enkripsi dan masukkan passphrase yang sama dulu (tombol Encryption).');
        }
        const cipher = this.cipher;
        return (blob) => decryptBlob(cipher, blob);
    }
    
    constructor(userId, storage, fileTree) {
        this.userId = userId;
        this.discordFileStorage = storage;
        this.fileTree = fileTree;
        // Nomor versi tree di memori (naik tiap perubahan) - untuk tahu apakah
        // tree berubah lagi selama menunggu balasan simpan dari bot.
        this._version = 0;
        this._listeners = new Set();
        // Bertugas mengirim salinan index (file tree) ke bot setiap kali ada
        // perubahan, dan mengambilnya kembali lewat bot jika perlu.
        this.indexBackup = new IndexBackupManager(userId, {
            getState: () => ({ tree: this.fileTree, version: this._version, baseMessageId: this.getBase() }),
            onSaved: (result) => this._onBackupSaved(result),
            onStatus: (status, error) => this._emit({ type: 'backup-status', status, error }),
        });
        // Penghitung operasi tulis yang SEDANG BERJALAN (hapus/upload, yang
        // butuh beberapa detik karena banyak request ke Discord & bisa kena
        // rate limit). Selama > 0, hasil gabungan (merge) dari bot tidak
        // langsung dipasang supaya tidak menabrak operasi yang belum selesai.
        this._activeMutations = 0;
        // Simpanan tree besar yang masih menunggu ikut disimpan saat halaman ditutup.
        if (typeof window !== 'undefined') window.addEventListener('pagehide', () => this._flushPersist());
    }

    // ===================== Pemberitahuan ke UI =====================
    // listener(event): event.type = 'tree-replaced' (tree diganti hasil
    // sinkronisasi/gabungan, UI perlu refresh) | 'backup-status'.
    subscribe(listener) {
        this._listeners.add(listener);
        return () => this._listeners.delete(listener);
    }
    _emit(event) {
        for (const listener of this._listeners) {
            try {
                listener(event);
            } catch (error) {
                console.warn('[Disbox] Listener error:', error);
            }
        }
    }

    // ===================== Status sinkronisasi (per perangkat) =====================
    // base  = messageId backup yang menjadi dasar tree di perangkat ini
    // dirty = ada perubahan di perangkat ini yang belum dikonfirmasi tersimpan oleh bot
    getBase() {
        return localStorage.getItem(`disbox_${this.userId}_base`) || null;
    }
    _setBase(messageId) {
        if (messageId) localStorage.setItem(`disbox_${this.userId}_base`, messageId);
        else localStorage.removeItem(`disbox_${this.userId}_base`);
    }
    isDirty() {
        return localStorage.getItem(`disbox_${this.userId}_dirty`) === '1';
    }
    _setDirty(dirty) {
        if (dirty) localStorage.setItem(`disbox_${this.userId}_dirty`, '1');
        else localStorage.removeItem(`disbox_${this.userId}_dirty`);
    }
    // Simpan tree di browser (lihat tree-store.js). Tree kecil langsung
    // disimpan; tree besar (JSON puluhan MB) digabung: simpan beruntun, misal
    // saat menghapus banyak file, jadi satu kali simpan.
    _persistTree(savedAt = Date.now()) {
        this._pendingPersistAt = savedAt;
        if (this._lastPersistChars > LOCAL_MAX_CHARS) {
            if (!this._persistTimer) this._persistTimer = setTimeout(() => this._flushPersist(), 1500);
            return;
        }
        this._flushPersist();
    }
    _flushPersist() {
        clearTimeout(this._persistTimer);
        this._persistTimer = null;
        const savedAt = this._pendingPersistAt;
        if (savedAt === undefined) return Promise.resolve();
        this._pendingPersistAt = undefined;
        this._updateUsage();
        return saveTree(this.userId, this.fileTree, { base: this.getBase(), savedAt })
            .then(({ chars }) => { this._lastPersistChars = chars; });
    }
    // Total ukuran file akun ini, untuk "Total Upload" di navbar.
    _updateUsage() {
        try {
            localStorage.setItem(`disbox_${this.userId}_usedBytes`, String(this._totalBytes()));
        } catch { /* hanya tampilan */ }
        if (typeof window !== 'undefined') window.dispatchEvent(new Event('disbox-usage'));
    }
    // Ganti seluruh tree (hasil sinkronisasi dari bot), lalu beri tahu UI.
    _replaceTree(tree, { savedAt, reason, summary } = {}) {
        this.fileTree = tree;
        this._version++;
        this._persistTree(savedAt || Date.now());
        this._emit({ type: 'tree-replaced', reason, summary });
    }
    _onBackupSaved({ messageId, merged, index, summary, postedBaseId, version, timestamp }) {
        const unchangedSincePost = version === this._version;
        if (merged && index) {
            if (unchangedSincePost && this._activeMutations === 0) {
                this._setBase(messageId);
                this._setDirty(false);
                this._replaceTree(index, { savedAt: timestamp, reason: 'merged', summary });
            } else {
                // Tree berubah lagi selama menunggu (atau ada upload/hapus yang
                // masih berjalan): gabungkan lagi di pengiriman berikutnya
                // dengan dasar = versi yang barusan dikirim.
                this._setBase(postedBaseId || messageId);
                if (this._activeMutations > 0) this._resyncAfterMutations = true;
                else this.indexBackup.schedule(1000);
            }
            return;
        }
        this._setBase(messageId);
        if (unchangedSincePost) this._setDirty(false);
        else this.indexBackup.schedule(1000);
    }
    _beginMutation() {
        this._activeMutations++;
    }
    _endMutation() {
        this._activeMutations--;
        if (this._activeMutations === 0 && this._resyncAfterMutations) {
            this._resyncAfterMutations = false;
            this.indexBackup.schedule(500);
        }
    }
    // Menyimpan file tree ke localStorage (state utama) DAN menjadwalkan
    // backup otomatis ke Discord (lihat index-backup.js). Dipanggil setiap
    // kali file tree berubah (upload, hapus, rename, pindah, dll).
    //
    // Juga mencatat KAPAN perubahan lokal ini terjadi (`_savedAt`), supaya
    // saat device lain (atau device ini sendiri di sesi lain) mengecek "siapa
    // yang lebih baru - data lokal atau backup Discord", perbandingannya akurat.
    _saveTree() {
        this._version++;
        this._persistTree();
        this._setDirty(true);
        this.indexBackup.schedule();
        this._emit({ type: 'backup-status', status: 'pending' });
    }
    exportIndex() {
        return JSON.stringify(this.fileTree, null, 2);
    }
    getLocalSavedAt() {
        return Number(localStorage.getItem(`disbox_${this.userId}_savedAt`)) || 0;
    }
    // Restore dari file JSON manual: dianggap perubahan baru di perangkat ini
    // (disimpan lokal DAN dikirim sebagai backup terbaru).
    importIndex(jsonString) {
        const parsed = JSON.parse(jsonString);
        if (!parsed || parsed.type !== "directory") {
            throw new Error("File index tidak valid.");
        }
        this.fileTree = parsed;
        this._saveTree();
    }
    // Tombol "Restore from Discord": ambil backup TERBARU di Discord dan pakai
    // apa adanya, membuang perubahan di perangkat ini yang belum tersimpan.
    async restoreFromDiscordBackup() {
        const latest = await this.indexBackup.fetchLatest();
        this._setBase(latest.messageId);
        this._setDirty(false);
        this._replaceTree(latest.tree, { savedAt: latest.timestamp, reason: 'restore' });
        return this.fileTree;
    }
    // Sinkronisasi saat app dibuka / login:
    // - Ada perubahan di perangkat ini yang belum tersimpan -> kirim dulu; bot
    //   menggabungkannya dengan perubahan dari perangkat lain (tidak ada yang
    //   hilang), lalu hasil gabungannya dipakai di sini.
    // - Tidak ada -> pakai versi terbaru dari Discord kalau berbeda.
    async syncFromDiscordIfNewer() {
        if (this._activeMutations > 0) {
            return { applied: false, error: null };
        }
        let base = this.getBase();
        let dirty = this.isDirty();
        let latest;
        try {
            latest = await this.indexBackup.fetchLatest();
        } catch (e) {
            if (e && e.noBackup) {
                // Akun baru / belum pernah backup: kirim tree lokal kalau ada isinya.
                if (dirty || Object.keys(this.fileTree.children || {}).length > 0) {
                    this._setDirty(true);
                    await this.indexBackup.flushNow();
                }
                return { applied: false, error: null };
            }
            return { applied: false, error: e };
        }
        // Perangkat yang dipakai sebelum pembaruan ini belum punya catatan
        // "base": anggap ada perubahan lokal kalau data lokal lebih baru dari
        // backup terbaru (cara lama), supaya perubahannya ikut digabungkan.
        if (!base && !dirty && this.getLocalSavedAt() > latest.timestamp) {
            dirty = true;
            this._setDirty(true);
        }
        if (dirty) {
            await this.indexBackup.flushNow();
            return { applied: true, error: null, merged: true };
        }
        if (latest.messageId === base) {
            return { applied: false, error: null };
        }
        this._setBase(latest.messageId);
        this._replaceTree(latest.tree, { savedAt: latest.timestamp, reason: 'sync' });
        return { applied: true, error: null };
    }

    // ===================== Riwayat versi (Time Machine) =====================
    async getVersionHistory() {
        return this.indexBackup.fetchHistory();
    }
    async getVersion(messageId) {
        return this.indexBackup.fetchVersion(messageId);
    }
    // Ganti SELURUH daftar file dengan versi lama (disimpan sebagai versi terbaru).
    restoreWholeVersion(tree) {
        if (!tree || tree.type !== 'directory') throw new Error('Versi ini tidak valid.');
        this.fileTree = JSON.parse(JSON.stringify(tree));
        this._saveTree();
        this._emit({ type: 'tree-replaced', reason: 'restore-version' });
    }
    // Kembalikan HANYA file yang ada di versi lama tapi hilang dari daftar
    // sekarang (dicek juga di Trash). File yang isinya sudah terhapus
    // permanen dari Discord dilewati (tidak bisa dipulihkan lagi).
    async restoreMissingFiles(tree, onProgress) {
        const present = new Set();
        const collect = (node) => {
            for (const name of Object.keys(node.children || {})) {
                const child = node.children[name];
                if (child.type === 'directory') collect(child);
                else {
                    const key = fileKeyOf(child);
                    if (key) present.add(key);
                }
            }
        };
        collect(this.fileTree);
        const missing = [];
        const walk = (node, nodePath) => {
            for (const name of Object.keys(node.children || {})) {
                const child = node.children[name];
                const childPath = `${nodePath}${FILE_DELIMITER}${name}`;
                if (nodePath === '' && name === TRASH_NAME) continue;
                if (child.type === 'directory') walk(child, childPath);
                else {
                    const key = fileKeyOf(child);
                    if (key && !present.has(key)) missing.push({ path: childPath, node: child });
                }
            }
        };
        walk(tree, '');
        let restored = 0;
        let skipped = 0;
        for (let i = 0; i < missing.length; i++) {
            const { path, node } = missing[i];
            if (onProgress) onProgress(i, missing.length);
            let alive = true;
            try {
                const refs = JSON.parse(node.content);
                await this.discordFileStorage.getAttachmentUrls([refs[0]]);
            } catch {
                alive = false;
            }
            if (!alive) {
                skipped++;
                continue;
            }
            const parts = path.split(FILE_DELIMITER).filter(Boolean);
            const name = parts.pop();
            let dir = this.fileTree;
            for (const part of parts) {
                if (!dir.children) dir.children = {};
                if (!dir.children[part] || dir.children[part].type !== 'directory') {
                    const partName = dir.children[part] ? `${part} (dipulihkan)` : part;
                    if (!dir.children[partName]) {
                        dir.children[partName] = {
                            id: Math.floor(Math.random() * 1000000), parent_id: dir.id, name: partName, type: 'directory',
                            created_at: new Date().toISOString(), updated_at: new Date().toISOString(), children: {},
                        };
                    }
                    dir = dir.children[partName];
                } else {
                    dir = dir.children[part];
                }
            }
            let finalName = name;
            let n = 1;
            while (dir.children && Object.prototype.hasOwnProperty.call(dir.children, finalName)) {
                const dot = name.lastIndexOf('.');
                finalName = dot > 0 ? `${name.slice(0, dot)} (dipulihkan ${n})${name.slice(dot)}` : `${name} (dipulihkan ${n})`;
                n++;
            }
            if (!dir.children) dir.children = {};
            dir.children[finalName] = { ...node, name: finalName, parent_id: dir.id };
            restored++;
        }
        if (onProgress) onProgress(missing.length, missing.length);
        if (restored > 0) {
            this._saveTree();
            this._emit({ type: 'tree-replaced', reason: 'restore-files' });
        }
        return { restored, skipped, candidates: missing.length };
    }

    // ===================== Bintang & Terbaru =====================
    async toggleStar(path) {
        const file = this.getFile(path, false);
        if (!file) throw new Error(`File not found: ${path}`);
        await this.updateFile(path, { starred: file.starred ? undefined : true, updated_at: file.updated_at });
        return Boolean(file.starred);
    }
    _walkItems(visit) {
        const walk = (node, nodePath) => {
            for (const name of Object.keys(node.children || {})) {
                if (nodePath === '' && name === TRASH_NAME) continue;
                const child = node.children[name];
                const childPath = `${nodePath}${FILE_DELIMITER}${name}`;
                visit(child, childPath);
                if (child.type === 'directory') walk(child, childPath);
            }
        };
        walk(this.fileTree, '');
    }
    getStarredItems() {
        const items = [];
        this._walkItems((node, path) => {
            if (node.starred) items.push(this.toRow(node, path));
        });
        return items;
    }
    getRecentFiles(limit = 60) {
        const files = [];
        this._walkItems((node, path) => {
            if (node.type !== 'directory' && node.content) files.push({ ...node, path });
        });
        files.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        return files.slice(0, limit);
    }
    // Cari file berdasarkan identitas chunk-nya (tetap ketemu walau sudah di-rename/dipindah).
    findFileByKey(key) {
        let found = null;
        this._walkItems((node, path) => {
            if (!found && node.type !== 'directory' && fileKeyOf(node) === key) found = { ...node, path };
        });
        return found;
    }

    // ===================== Upload yang belum selesai (bisa dilanjutkan) =====================
    getPendingUploads() {
        return listPendingUploads(this.userId);
    }
    // Buang upload tertunda: potongan yang sudah terkirim dihapus dari Discord di latar belakang.
    // Discord menolak potongan sebesar `failedBytes`. Bot menurunkan ukuran
    // potongan akun (dipakai bersama web & WebDAV); kalau bot tidak bisa
    // dihubungi, diturunkan di perangkat ini saja. Mengembalikan ukuran baru,
    // atau 0 kalau tidak bisa lebih kecil lagi.
    async lowerChunkSize(failedBytes) {
        const storage = this.discordFileStorage;
        try {
            storage.setChunkSize((await reportUploadTooLarge(this.userId, failedBytes)).uploadChunkBytes);
        } catch (error) {
            if (error.status === 409) return 0;
            const next = CHUNK_LADDER.find((bytes) => bytes < failedBytes);
            if (next) storage.setChunkSize(Math.min(storage.chunkBytes, next));
        }
        return storage.chunkBytes < failedBytes ? storage.chunkBytes : 0;
    }
    discardPendingUpload(id) {
        const record = getPendingUpload(this.userId, id);
        removePendingUpload(this.userId, id);
        if (!record) return;
        if (record.refs) {
            const refs = Object.values(record.refs);
            if (refs.length > 0) this.discordFileStorage.delete(refs).catch(() => {});
        }
        // Entri file kosong (placeholder) yang tertinggal kalau tab tertutup di tengah upload.
        const node = this.getFile(record.path, false);
        if (node && node.type !== 'directory' && !node.content) {
            const parent = this.getParent(record.path) || this.fileTree;
            if (parent && parent.children) {
                delete parent.children[node.name];
                this._saveTree();
            }
        }
    }
    // Melanjutkan upload tertunda dengan File yang dipilih ulang (setelah
    // halaman di-reload). Tanggal file di beberapa HP bisa berubah saat
    // dipilih ulang, jadi catatan dipindah ke identitas file yang baru.
    rekeyPendingUpload(oldId, file) {
        const record = getPendingUpload(this.userId, oldId);
        if (!record) return null;
        const newId = uploadIdFor(record.path, file);
        if (newId !== oldId) {
            removePendingUpload(this.userId, oldId);
            savePendingUpload(this.userId, { ...record, id: newId, lastModified: file.lastModified || 0 });
        }
        return record;
    }
    // Upload tertunda yang sudah lebih dari 14 hari dibuang otomatis.
    purgeStalePendingUploads(maxAgeMs = 14 * 24 * 60 * 60 * 1000) {
        for (const record of listPendingUploads(this.userId)) {
            if (Date.now() - record.updatedAt > maxAgeMs) this.discardPendingUpload(record.id);
        }
    }
    getFile(path, copy = true) {
        let file = this.fileTree;
        let pathParts = path.split(FILE_DELIMITER);
        pathParts.shift();
        for (let i = 0; i < pathParts.length; i++) {
            if (file.children && file.children[pathParts[i]]) {
                file = file.children[pathParts[i]];
            } else {
                return null;
            }
        }
        if (copy) {
            return { ...file, path };
        } else {
            return file;
        }
    }
    // Total isi tiap folder (termasuk subfolder), dihitung sekali per versi
    // tree: dipanggil tiap tampilan digambar ulang, dan akun besar punya
    // ratusan ribu file. Map: node folder -> jumlah byte.
    _folderSizes() {
        const cache = this._usageCache;
        if (cache && cache.tree === this.fileTree && cache.version === this._version) return cache.sizes;
        const sizes = new WeakMap();
        const walk = (node) => {
            let bytes = 0;
            for (const name in node.children || {}) {
                const child = node.children[name];
                if (child.type === 'directory') bytes += walk(child);
                else bytes += Number(child.size) || 0;
            }
            sizes.set(node, bytes);
            return bytes;
        };
        walk(this.fileTree);
        this._usageCache = { tree: this.fileTree, version: this._version, sizes };
        return sizes;
    }
    _totalBytes() {
        return this._folderSizes().get(this.fileTree) || 0;
    }
    // Baris tabel untuk satu item: folder diberi `size` = total isinya.
    toRow(node, path) {
        const row = { ...node, path };
        if (node.type === 'directory') row.size = this._folderSizes().get(node) || 0;
        return row;
    }
    // Daftar datar semua file & folder (tanpa Trash) untuk pencarian, dibuat
    // sekali per versi tree. Lihat search.js.
    searchIndex() {
        const cache = this._searchCache;
        if (cache && cache.tree === this.fileTree && cache.version === this._version) return cache.entries;
        const entries = [];
        const walk = (node, nodePath, parentKey) => {
            for (const name in node.children || {}) {
                if (node === this.fileTree && name === TRASH_NAME) continue;
                const child = node.children[name];
                const childPath = `${nodePath}${FILE_DELIMITER}${name}`;
                entries.push({
                    node: child,
                    path: childPath,
                    name,
                    type: child.type,
                    key: normalize(name),
                    parentKey,
                    ext: child.type === 'directory' ? '' : extensionOf(name),
                });
                if (child.type === 'directory') walk(child, childPath, `${parentKey}/${normalize(name)}`);
            }
        };
        walk(this.fileTree, '', '');
        this._searchCache = { tree: this.fileTree, version: this._version, entries };
        return entries;
    }
    getTotalStorageUsed() {
        const totalBytes = this._totalBytes();
        if (totalBytes === 0) return "0 Bytes";
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(totalBytes) / Math.log(k));
        return parseFloat((totalBytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }
    getChildren(path) {
        let children = {};
        if (path === '') {
            children = this.fileTree.children || {};
        } else {
            const file = this.getFile(path);
            if (!file) {
                throw new Error(`File not found: ${path}`);
            }
            if (file.type !== 'directory') {
                throw new Error(`File is not a directory: ${path}`);
            }
            children = file.children || {};
        }
        const parsedChildren = {};
        for (let child in children) {
            // Folder Trash disembunyikan dari daftar folder biasa - diakses
            // lewat getTrashChildren() saja, lewat tombol "Trash" khusus.
            if (path === '' && child === TRASH_NAME) continue;
            const childPath = `${path}${FILE_DELIMITER}${child}`;
            parsedChildren[child] = this.toRow(children[child], childPath);
        }
        return parsedChildren;
    }
    // Isi folder Trash (dipakai tampilan "Trash"). Aman dipanggil walau belum
    // pernah ada yang dihapus sama sekali (folder Trash belum dibuat).
    getTrashChildren() {
        const trash = this.getFile(TRASH_PATH, false);
        if (!trash) return {};
        const parsedChildren = {};
        for (let child in (trash.children || {})) {
            parsedChildren[child] = this.toRow(trash.children[child], `${TRASH_PATH}${FILE_DELIMITER}${child}`);
        }
        return parsedChildren;
    }
    isTrashEmpty() {
        const trash = this.getFile(TRASH_PATH, false);
        return !trash || !trash.children || Object.keys(trash.children).length === 0;
    }
    getParent(path) {
        if (!path.includes(FILE_DELIMITER)) {
            return null;
        }
        if (path.split(FILE_DELIMITER).length === 2) {
            return this.fileTree;
        }
        const parentPath = path.split(FILE_DELIMITER).slice(0, -1).join(FILE_DELIMITER);
        return this.getFile(parentPath);
    }
    async updateFile(path, changes) {
        const file = this.getFile(path, false);
        if (!file) {
            throw new Error(`File not found: ${path}`);
        }
        if (!("updated_at" in changes)) {
            changes.updated_at = new Date().toISOString();
        }
        for (let key in changes) {
            file[key] = changes[key];
        }
        this._saveTree();
        return file;
    }
    async renameFile(path, newName) {
        if (path === TRASH_PATH) {
            throw new Error('Folder Trash tidak bisa di-rename.');
        }
        const isRoot = !path.includes(FILE_DELIMITER, 1);
        if (isRoot && newName === TRASH_NAME) {
            throw new Error(`Nama "${TRASH_NAME}" dipakai khusus untuk folder Trash, gunakan nama lain.`);
        }
        const file = this.getFile(path);
        if (!file) {
            throw new Error(`File not found: ${path}`);
        }
        if (!newName || newName.includes(FILE_DELIMITER)) {
            throw new Error(`Nama tidak boleh kosong atau mengandung "${FILE_DELIMITER}".`);
        }
        const newPath = path.substring(0, path.lastIndexOf(FILE_DELIMITER) + 1) + newName;
        const newFile = this.getFile(newPath);
        if (newFile) {
            throw new Error(`File already exists: ${newPath}`);
        }
        const changes = await this.updateFile(file.path, { name: newName });
        const parent = this.getParent(path) || this.fileTree;
        delete parent.children[file.name];
        parent.children[changes.name] = changes;
        this._saveTree();
        return this.getFile(newPath);
    }
    // Memindahkan file/folder ke folder lain. `newParentPath` "" berarti root.
    // Yang dipindah adalah node ASLI di tree (bukan salinan), jadi seluruh isi
    // folder ikut pindah dan tidak ada data yang tersalin/hilang. Isi file di
    // Discord tidak disentuh, hanya posisinya di index yang berubah.
    async moveFile(path, newParentPath) {
        if (path === TRASH_PATH) {
            throw new Error('Folder Trash tidak bisa dipindah.');
        }
        if (newParentPath === TRASH_PATH || newParentPath.startsWith(TRASH_PATH + FILE_DELIMITER)) {
            throw new Error('Tidak bisa memindah langsung ke folder Trash. Gunakan tombol Delete.');
        }
        const file = this.getFile(path, false);
        if (!file) {
            throw new Error(`File not found: ${path}`);
        }
        const newParent = this.getFile(newParentPath, false);
        if (!newParent) {
            throw new Error(`Parent directory not found: ${newParentPath}`);
        }
        if (newParent.type !== 'directory') {
            throw new Error(`Parent is not a directory: ${newParentPath}`);
        }
        if (newParentPath === path || newParentPath.startsWith(path + FILE_DELIMITER)) {
            throw new Error(`Folder tidak bisa dipindah ke dalam dirinya sendiri: ${path}`);
        }
        const currentParentPath = path.split(FILE_DELIMITER).slice(0, -1).join(FILE_DELIMITER);
        if (currentParentPath === newParentPath) {
            return file;
        }
        if (!newParent.children) newParent.children = {};
        if (Object.prototype.hasOwnProperty.call(newParent.children, file.name)) {
            throw new Error(`File already exists: ${newParentPath}${FILE_DELIMITER}${file.name}`);
        }
        const parent = this.getParent(path) || this.fileTree;
        delete parent.children[file.name];
        newParent.children[file.name] = file;
        file.parent_id = newParent.id;
        file.updated_at = new Date().toISOString();
        this._saveTree();
        return file;
    }
    // Daftar path semua folder (dipakai dialog "Move"), root = "".
    getAllDirectoryPaths() {
        const result = [""];
        const walk = (node, nodePath) => {
            for (const name of Object.keys(node.children || {})) {
                if (nodePath === "" && name === TRASH_NAME) continue; // Trash bukan tujuan pindah yang valid
                const child = node.children[name];
                if (child && child.type === 'directory') {
                    const childPath = `${nodePath}${FILE_DELIMITER}${name}`;
                    result.push(childPath);
                    walk(child, childPath);
                }
            }
        };
        walk(this.fileTree, "");
        return result;
    }
    // Mengumpulkan semua file (bukan folder) di dalam sebuah folder secara rekursif.
    // Dipakai untuk menghitung total chunk agar progress penghapusan folder akurat.
    collectAllFiles(path) {
        let files = [];
        const children = this.getChildren(path);
        for (let name in children) {
            const child = children[name];
            if (child.type === 'directory') {
                files = files.concat(this.collectAllFiles(child.path));
            } else {
                files.push(child);
            }
        }
        return files;
    }
    // Folder Trash sendiri dibuat malas (baru dibuat pas pertama kali ada yang
    // dihapus), supaya akun yang belum pernah menghapus apa pun tidak punya
    // folder Trash kosong nongol di index-nya.
    _ensureTrashFolder() {
        if (!this.fileTree.children) this.fileTree.children = {};
        if (!this.fileTree.children[TRASH_NAME]) {
            this.fileTree.children[TRASH_NAME] = {
                id: Math.floor(Math.random() * 1000000),
                parent_id: this.fileTree.id,
                name: TRASH_NAME,
                type: 'directory',
                children: {},
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
            };
        }
        return this.fileTree.children[TRASH_NAME];
    }
    // "Menghapus" file/folder sebenarnya cuma MEMINDAHKANNYA ke folder Trash
    // (chunk-nya di Discord TIDAK disentuh) - supaya bisa dibatalkan lewat
    // Restore kalau salah hapus. Baru benar-benar dihapus dari Discord lewat
    // permanentlyDeleteFile() / emptyTrash() di bawah.
    async deleteFile(path, onProgress) {
        if (path === TRASH_PATH) {
            throw new Error('Folder Trash tidak bisa dihapus langsung. Gunakan "Kosongkan Trash".');
        }
        this._beginMutation();
        try {
            const file = this.getFile(path, false);
            if (!file) {
                throw new Error(`File not found: ${path}`);
            }
            const trash = this._ensureTrashFolder();
            const parent = this.getParent(path) || this.fileTree;
            if (parent && parent.children) delete parent.children[file.name];

            const originalName = file.name;
            const originalParentPath = path.split(FILE_DELIMITER).slice(0, -1).join(FILE_DELIMITER);

            // Hindari tabrakan nama di dalam Trash (misal menghapus "foto.jpg"
            // dari dua folder berbeda) - nama aslinya disimpan di
            // `original_name` supaya Restore mengembalikan nama yang benar.
            let trashName = originalName;
            let n = 1;
            while (Object.prototype.hasOwnProperty.call(trash.children, trashName)) {
                trashName = `${originalName} (${n++})`;
            }

            file.name = trashName;
            file.parent_id = trash.id;
            file.deleted_at = new Date().toISOString();
            file.original_path = originalParentPath;
            file.original_name = originalName;
            trash.children[trashName] = file;

            this._saveTree();
            if (onProgress) onProgress(1, 1);
            return file;
        } finally {
            this._endMutation();
        }
    }
    // Mengembalikan item dari Trash ke lokasi asalnya (root kalau folder
    // asalnya sudah tidak ada lagi), memakai nama aslinya lagi (ditambah
    // " (1)", " (2)", dst kalau sudah ada nama yang sama di sana).
    async restoreFile(trashPath) {
        this._beginMutation();
        try {
            const file = this.getFile(trashPath, false);
            if (!file) {
                throw new Error(`File not found: ${trashPath}`);
            }
            const trash = this.getFile(TRASH_PATH, false);
            if (!trash || file.parent_id !== trash.id) {
                throw new Error('File ini tidak ada di Trash.');
            }
            let destParentPath = file.original_path || '';
            let destParent = destParentPath === '' ? this.fileTree : this.getFile(destParentPath, false);
            if (!destParent || destParent.type !== 'directory') {
                destParentPath = '';
                destParent = this.fileTree;
            }
            const restoredName = file.original_name || file.name;
            if (!destParent.children) destParent.children = {};
            let finalName = restoredName;
            let n = 1;
            while (Object.prototype.hasOwnProperty.call(destParent.children, finalName)) {
                finalName = `${restoredName} (${n++})`;
            }

            delete trash.children[file.name];
            file.name = finalName;
            file.parent_id = destParent.id;
            file.updated_at = new Date().toISOString();
            delete file.deleted_at;
            delete file.original_path;
            delete file.original_name;
            destParent.children[finalName] = file;

            this._saveTree();
            return { ...file, path: `${destParentPath}${FILE_DELIMITER}${finalName}` };
        } finally {
            this._endMutation();
        }
    }
    // Menghapus SATU item di dalam Trash secara PERMANEN (chunk-nya beneran
    // dihapus dari Discord - tidak bisa dikembalikan lagi setelah ini).
    async permanentlyDeleteFile(trashPath, onProgress) {
        const file = this.getFile(trashPath, false);
        if (!file) {
            throw new Error(`File not found: ${trashPath}`);
        }
        const trash = this.getFile(TRASH_PATH, false);
        if (!trash || file.parent_id !== trash.id) {
            throw new Error('File ini tidak ada di Trash.');
        }
        await this._hardDelete(trashPath, onProgress);
    }
    // Mengosongkan SELURUH isi Trash secara PERMANEN.
    async emptyTrash(onProgress) {
        const trashChildren = this.getTrashChildren();
        const names = Object.keys(trashChildren);
        if (onProgress) onProgress(0, Math.max(1, names.length));
        for (let i = 0; i < names.length; i++) {
            await this._hardDelete(trashChildren[names[i]].path, null);
            if (onProgress) onProgress(i + 1, names.length);
        }
    }
    // Penghapusan PERMANEN yang sesungguhnya (chunk di Discord benar-benar
    // dihapus) - ini dulu isi deleteFile() sebelum ada Trash. Hanya dipanggil
    // lewat permanentlyDeleteFile()/emptyTrash() di atas, dari dalam Trash.
    async _hardDelete(path, onProgress) {
        this._beginMutation();
        try {
            const file = this.getFile(path);
            if (!file) {
                throw new Error(`File not found: ${path}`);
            }
            if (file.type === 'directory') {
                // Folder kini bisa langsung dihapus walau masih berisi file/subfolder;
                // seluruh isinya akan dihapus secara rekursif terlebih dahulu.
                await this.deleteFolderRecursive(path, onProgress);
                this._saveTree();
                return;
            }
            if (file.content) {
                // Kalau field `content` rusak/format lama (misal dari upload yang
                // terputus, atau hasil restore index versi lama) - JANGAN sampai
                // ini memblokir penghapusan LOKAL selamanya. Chunk yang gagal
                // dibersihkan di Discord cuma jadi sampah kecil di sana, jauh
                // lebih baik daripada file yang tidak bisa dihapus sama sekali.
                try {
                    await this.discordFileStorage.delete(JSON.parse(file.content), onProgress);
                } catch (error) {
                    console.warn(`[Disbox] Gagal membersihkan chunk Discord untuk "${file.name}" (data content mungkin rusak/format lama), melanjutkan hapus dari daftar:`, error);
                    if (onProgress) onProgress(1, 1);
                }
            } else if (onProgress) {
                onProgress(1, 1);
            }
            const parent = this.getParent(path) || this.fileTree;
            delete parent.children[file.name];
            this._saveTree();
        } finally {
            this._endMutation();
        }
    }
    // Menghapus sebuah folder beserta seluruh isinya (file dan subfolder) secara rekursif.
    // Node folder/file dari tree dilepas dari memori dulu di awal supaya UI/getFile tidak
    // lagi menunjuk ke data yang sedang dihapus, baru storage Discord-nya dibersihkan.
    async deleteFolderRecursive(path, onProgress) {
        const folder = this.getFile(path);
        if (!folder) {
            return;
        }
        // Hitung total chunk dari seluruh file di dalam folder ini agar progress
        // bar merepresentasikan keseluruhan proses, bukan per file.
        const allFiles = this.collectAllFiles(path);
        let totalChunks = 0;
        for (let f of allFiles) {
            if (f.content) {
                try {
                    totalChunks += JSON.parse(f.content).length;
                } catch (e) {
                    totalChunks += 1;
                }
            }
        }
        if (totalChunks === 0) {
            totalChunks = 1;
        }

        // Lepas folder dari tree induknya SEKARANG, sebelum penghapusan chunk selesai,
        // supaya UI langsung menganggap folder ini hilang meski proses masih berjalan
        // di background (mirip perilaku file tunggal).
        const parent = this.getParent(path) || this.fileTree;
        if (parent && parent.children) {
            delete parent.children[folder.name];
        }

        let chunksDeleted = 0;
        if (onProgress) {
            onProgress(0, totalChunks);
        }
        for (let f of allFiles) {
            const chunksDeletedBeforeThisFile = chunksDeleted;
            if (f.content) {
                // Sama seperti deleteFile() - satu file dengan `content` rusak
                // di dalam folder ini TIDAK BOLEH menggagalkan penghapusan
                // seluruh folder & file lain di dalamnya yang masih normal.
                try {
                    const parsed = JSON.parse(f.content);
                    await this.discordFileStorage.delete(parsed, (value) => {
                        if (onProgress) {
                            onProgress(chunksDeletedBeforeThisFile + value, totalChunks);
                        }
                    });
                    chunksDeleted += parsed.length;
                } catch (error) {
                    console.warn(`[Disbox] Gagal membersihkan chunk Discord untuk "${f.name}" di dalam folder (data content mungkin rusak/format lama), melanjutkan hapus dari daftar:`, error);
                    chunksDeleted += 1;
                }
            } else {
                chunksDeleted += 1;
            }
            if (onProgress) {
                onProgress(chunksDeleted, totalChunks);
            }
        }
        if (onProgress) {
            onProgress(totalChunks, totalChunks);
        }
    }
    async createFile(path, type = "file") {
        if (path === TRASH_PATH) {
            throw new Error(`Nama "${TRASH_NAME}" dipakai khusus untuk folder Trash, gunakan nama lain.`);
        }
        const file = this.getFile(path);
        if (file) {
            throw new Error(`File already exists: ${path}`);
        }
        const name = path.split(FILE_DELIMITER).slice(-1)[0];
        let parentFile = this.getParent(path) || this.fileTree;
        const extra = type === 'directory' ? { children: {} } : {};
        const newFileId = Math.floor(Math.random() * 1000000);
        const newFile = {
            id: newFileId,
            parent_id: parentFile.id,
            name: name,
            type: type,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            ...extra
        };
        if (!parentFile.children) {
            parentFile.children = {};
        }
        parentFile.children[name] = newFile;
        this._saveTree();
        return this.getFile(path);
    }
    async createDirectory(path) {
        const result = await this.createFile(path, 'directory');
        return result; 
        // Dihapus: window.location.reload()
    }
    // Upload file ke `path`. BISA DILANJUTKAN: setiap potongan yang selesai
    // dicatat (upload-store.js), jadi kalau koneksi putus ("Failed to
    // fetch"), tab tertutup, atau upload dijeda, memanggil uploadFile lagi
    // dengan path & file yang sama hanya mengirim potongan yang belum
    // terkirim. options.signal (AbortSignal) untuk menjeda.
    async uploadFile(path, fileBlob, onProgress, options = {}) {
        // Sama seperti deleteFile - upload chunk besar/banyak bisa makan waktu
        // lama, jadi hasil sinkronisasi tidak dipasang sampai selesai (lihat
        // `_activeMutations` di constructor).
        this._beginMutation();
        let createdPlaceholder = false;
        try {
            let file = this.getFile(path);
            if (!file) {
                await this.createFile(path);
                file = this.getFile(path);
                createdPlaceholder = true;
            }
            if (file.type === 'directory') {
                throw new Error(`Directory can't have content: ${path}`);
            }
            const cipher = this.cipher;
            const uploadId = uploadIdFor(path, fileBlob);
            let record = getPendingUpload(this.userId, uploadId);
            if (record && (Boolean(record.enc) !== Boolean(cipher) || record.size !== fileBlob.size)) {
                // Pengaturan enkripsi berubah sejak percobaan sebelumnya: mulai dari awal.
                this.discardPendingUpload(uploadId);
                record = null;
            }
            const newRecord = () => {
                const stored = this.discordFileStorage.chunkBytes;
                const chunkSize = cipher ? stored - ENC_OVERHEAD : stored;
                const created = {
                    id: uploadId,
                    path,
                    name: fileBlob.name || path.split(FILE_DELIMITER).pop(),
                    size: fileBlob.size,
                    lastModified: fileBlob.lastModified || 0,
                    chunkSize,
                    totalChunks: Math.ceil(fileBlob.size / chunkSize),
                    enc: Boolean(cipher),
                    fileId: file.id,
                    refs: {},
                    createdAt: Date.now(),
                };
                if (created.totalChunks > 1) savePendingUpload(this.userId, created);
                return created;
            };
            if (!record) record = newRecord();
            let contentReferences;
            try {
                for (let attempt = 0; ; attempt++) {
                    try {
                        contentReferences = await this.discordFileStorage.upload(fileBlob, record.fileId, onProgress, cipher, {
                            existingRefs: record.refs,
                            chunkSize: record.chunkSize,
                            signal: options.signal,
                            onChunkDone: record.totalChunks > 1
                                ? (index, ref) => recordUploadedChunk(this.userId, uploadId, index, ref)
                                : null,
                        });
                        break;
                    } catch (error) {
                        if (!error.tooLarge || attempt >= 6 || !(await this.lowerChunkSize(error.chunkBytes))) throw error;
                        // Discord menolak ukuran potongan ini (batas upload turun).
                        // Potongan satu file harus sama besar, jadi yang sudah
                        // terkirim dibuang dan file dikirim ulang dengan ukuran baru.
                        const pending = getPendingUpload(this.userId, uploadId);
                        removePendingUpload(this.userId, uploadId);
                        const sent = pending && pending.refs ? Object.values(pending.refs) : [];
                        if (sent.length > 0) this.discordFileStorage.delete(sent).catch(() => {});
                        console.warn(`[Disbox] Discord menolak potongan ${(error.chunkBytes / MiB).toFixed(1)} MB, upload diulang dengan potongan ${(this.discordFileStorage.chunkBytes / MiB).toFixed(1)} MB.`);
                        record = newRecord();
                    }
                }
            } catch (error) {
                if (createdPlaceholder) {
                    // Upload gagal & file ini baru dibuat SEKARANG (belum pernah
                    // punya isi) - buang lagi placeholder-nya, supaya tidak
                    // ketinggalan entri file kosong/rusak di daftar. Potongan
                    // yang sudah terkirim tetap tercatat untuk dilanjutkan.
                    const parent = this.getParent(path) || this.fileTree;
                    if (parent && parent.children) delete parent.children[file.name];
                    this._saveTree();
                }
                const pending = getPendingUpload(this.userId, uploadId);
                error.resumable = Boolean(pending);
                error.uploadId = uploadId;
                error.uploadedChunks = pending ? Object.keys(pending.refs || {}).length : 0;
                throw error;
            }
            // `enc` + `enc_chunk` (ukuran chunk ASLI) menandai file terenkripsi; file
            // yang ditimpa tanpa enkripsi menghapus tanda itu. `size` tetap ukuran asli.
            await this.updateFile(file.path, {
                size: fileBlob.size,
                content: JSON.stringify(contentReferences),
                enc: record.enc ? 1 : undefined,
                enc_chunk: record.enc ? record.chunkSize : undefined,
            });
            removePendingUpload(this.userId, uploadId);
            if (onProgress) {
                onProgress(1, 1);
            }
            return this.getFile(path);
        } finally {
            this._endMutation();
        }
    }
    async downloadFile(path, writeStream, onProgress) {
        const file = this.getFile(path);
        if (!file) {
            throw new Error(`File not found: ${path}`);
        }
        if (file.type === 'directory') {
            throw new Error(`Cannot download content from directory: ${path}`);
        }
        const decrypt = this.getDecryptor(file);
        const contentReferences = JSON.parse(file.content);
        await this.discordFileStorage.download(contentReferences, writeStream, onProgress, file.size, decrypt);
        if (onProgress) {
            onProgress(1, 1);
        }
    }
    async getAttachmentUrls(path) {
        const file = this.getFile(path);
        if (!file) {
            throw new Error(`File not found: ${path}`);
        }
        if (file.type === 'directory') {
            throw new Error(`Cannot share directory: ${path}`);
        }
        const contentReferences = JSON.parse(file.content);
        return await this.discordFileStorage.getAttachmentUrls(contentReferences);
    }
    async getPreviewBlob(path, mimeType, onProgress = null) {
        const file = this.getFile(path);
        if (!file) {
            throw new Error(`File not found: ${path}`);
        }
        if (file.type === 'directory') {
            throw new Error(`Cannot preview directory: ${path}`);
        }
        const decrypt = this.getDecryptor(file);
        const attachmentUrls = await this.getAttachmentUrls(path);
        return await blobFromAttachmentUrls(attachmentUrls, mimeType, onProgress, file.size, 6, decrypt);
    }
}
export default DisboxFileManager;